import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { CheckInEventType } from "@lib/infrastructure";
import type { HaNPersonRow } from "@lib/logic";
import { getCheckInStatusByUserIdOrDirectoryObjectId, useAppSelector } from "@lib/store";
import { Avatar } from "@molecules";

type PersonHeaderProps = {
  person: HaNPersonRow["owner"];
  children: ReactNode;
};

// The person's avatar and name, whether they're checked in, and how the user relates to them at the end
export const PersonHeader = ({ person, children }: PersonHeaderProps) => {
  const { t } = useTranslation();

  // Only known for the user's connections right now
  const isCheckedIn = useAppSelector(state => {
    return getCheckInStatusByUserIdOrDirectoryObjectId(state, person.id)?.eventType === CheckInEventType.AtTheOffice;
  });

  return (
    <div className="flex items-center gap-4">
      <Avatar
        className="flex-none"
        shouldShowCheckIn
        user={{
          id: person.id,
          email: person.email,
          initials: person.initials ?? "",
          name: person.name,
          imageHash: person.imageHash,
        }}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <h2 className="max-w-60 truncate font-display text-[26px] font-medium leading-8">{person.name}</h2>
        {isCheckedIn && <p className="text-mapiq-black-500">{t("hereAndNow:CheckedIn")}</p>}
      </div>

      <div className="flex-none">{children}</div>
    </div>
  );
};
